import React, { useEffect, useState } from 'react';
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay } from '@dnd-kit/core';
import { useFunnelStore } from './store/funnelStore';
import { createDefaultElement } from './utils/helpers';

import Canvas from './components/Layout/Canvas';

import PropertiesPanel from './components/Layout/PropertiesPanel';
import TutorialOverlay from './components/Layout/TutorialOverlay';


/**
 * Main App Component
 * Implements the 4-panel layout for the funnel builder
 */
const App: React.FC = () => {
  const { setIsDragging, selectedElementId, addElement, funnel, isPreviewMode } = useFunnelStore();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggedElementType, setDraggedElementType] = useState<string | null>(null);
  const [draggedElementLabel, setDraggedElementLabel] = useState<string | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [hasShownTutorial, setHasShownTutorial] = useState(false);
  const [activeIcon, setActiveIcon] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Check if user has added their first element
  useEffect(() => {
    const totalElements = funnel.sections.reduce((total, section) => {
      return total + section.columns.reduce((colTotal, col) => colTotal + col.elements.length, 0);
    }, 0);

    // Show tutorial when first element is added and tutorial hasn't been shown yet
    if (totalElements === 1 && !hasShownTutorial) {
      const timer = setTimeout(() => {
        setShowTutorial(true);
        setHasShownTutorial(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [funnel.sections, hasShownTutorial]);

  // Handle drawer open/close based on selected element
  useEffect(() => {
    if (selectedElementId && !isPreviewMode) {
      setIsDrawerOpen(true);
    } else {
      setIsDrawerOpen(false);
    }
  }, [selectedElementId, isPreviewMode]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Undo: Ctrl/Cmd + Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        useFunnelStore.getState().undo();
      }

      // Redo: Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y
      if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') ||
        ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
        e.preventDefault();
        useFunnelStore.getState().redo();
      }

      // Delete: Delete or Backspace (when element is selected)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElementId) {
        // Only delete if not focused on an input
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          useFunnelStore.getState().deleteElement(selectedElementId);
        }
      }

      // Close drawer: Escape key
      if (e.key === 'Escape' && isDrawerOpen) {
        e.preventDefault();
        useFunnelStore.getState().selectElement(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElementId, isDrawerOpen]);

  const handleDragStart = (event: DragStartEvent) => {
    // Don't allow dragging in preview mode
    if (isPreviewMode) return;

    setIsDragging(true);
    setActiveId(event.active.id as string);

    // Check if dragging a new element from library
    const data = event.active.data.current;
    if (data?.type === 'new-element') {
      setDraggedElementType(data.elementType);
      setDraggedElementLabel(data.elementLabel || data.elementType);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    // Don't allow dragging in preview mode
    if (isPreviewMode) return;

    setIsDragging(false);
    setActiveId(null);
    setDraggedElementType(null);
    setDraggedElementLabel(null);

    const { active, over } = event;

    if (!over) return;

    // Handle dropping new element from library
    const activeData = active.data.current;
    const overData = over.data.current;

    // Handle dropping into a nested column (within a column layout element)
    if (activeData?.type === 'new-element' && overData?.type === 'nested-column') {
      const elementType = activeData.elementType;
      const { parentElementId, nestedColumnId } = overData;

      // Create new element and add it to the nested column
      const newElement = createDefaultElement(elementType);
      useFunnelStore.getState().addElementToNestedColumn(parentElementId, nestedColumnId, newElement);

      // Auto-select the newly added element
      setTimeout(() => {
        useFunnelStore.getState().selectElement(newElement.id);
      }, 100);
      return;
    }

    // Handle dropping into a regular column
    if (activeData?.type === 'new-element' && overData?.type === 'column') {
      const elementType = activeData.elementType;
      const { sectionId, columnId } = overData;

      // Create new element and add it to the column
      const newElement = createDefaultElement(elementType);
      addElement(sectionId, columnId, newElement);

      // Auto-select the newly added element
      setTimeout(() => {
        useFunnelStore.getState().selectElement(newElement.id);
      }, 100);
    }
  };

  const handleCloseDrawer = () => {
    useFunnelStore.getState().selectElement(null);
  };

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex h-screen w-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden relative">

        {/* <div className="w-24 bg-white border-r border-gray-200 flex-shrink-0 flex flex-col items-center py-4 gap-2">

          <button
            onClick={() => setActiveIcon(activeIcon === 'play' ? null : 'play')}
            className={`h-[38px] w-[38px] rounded-[10px] border border-[#6F6F6F] flex items-center justify-center transition-colors cursor-pointer ${activeIcon === 'play' ? 'bg-[#003C07] border-green-600' : 'bg-white hover:bg-gray-50'
              }`}
          >
            <PlayIcon />
          </button>
          <button
            onClick={() => setActiveIcon(activeIcon === 'plus' ? null : 'plus')}
            className={`h-[38px] w-[38px] rounded-[10px] border border-[#6F6F6F] flex items-center justify-center transition-colors cursor-pointer ${activeIcon === 'plus' ? 'bg-[#003C07] border-[#003C07]' : 'bg-white hover:bg-gray-50'
              }`}
          >
            <PlusIcon />
          </button>
          <button
            onClick={() => setActiveIcon(activeIcon === 'text' ? null : 'text')}
            className={`h-[38px] w-[38px] rounded-[10px] border border-[#6F6F6F] flex items-center justify-center transition-colors cursor-pointer ${activeIcon === 'text' ? 'bg-[#003C07] border-[#003C07]' : 'bg-white hover:bg-gray-50'
              }`}
          >
            <TextIcon />
          </button>
          <button
            onClick={() => setActiveIcon(activeIcon === 'block' ? null : 'block')}
            className={`h-[38px] w-[38px] rounded-[10px] border border-[#6F6F6F] flex items-center justify-center transition-colors cursor-pointer ${activeIcon === 'block' ? 'bg-[#003C07] border-[#003C07]' : 'bg-white hover:bg-gray-50'
              }`}
          >
            <BlockIcon />
          </button>
          <button
            onClick={() => setActiveIcon(activeIcon === 'hand' ? null : 'hand')}
            className={`h-[38px] w-[38px] rounded-[10px] border border-[#6F6F6F] flex items-center justify-center transition-colors cursor-pointer ${activeIcon === 'hand' ? 'bg-[#003C07] border-[#003C07]' : 'bg-white hover:bg-gray-50'
              }`}
          >
            <HandIcon />
          </button>
          <button
            onClick={() => setActiveIcon(activeIcon === 'comment' ? null : 'comment')}
            className={`h-[38px] w-[38px] rounded-[10px] border border-[#6F6F6F] flex items-center justify-center transition-colors cursor-pointer ${activeIcon === 'comment' ? 'bg-[#003C07] border-[#003C07]' : 'bg-white hover:bg-gray-50'
              }`}
          >
            <CommentIcon />
          </button>

        </div> */}

        {/* Center Canvas */}
        <div className="flex-1 overflow-hidden">
          <Canvas />
        </div>

        {/* Backdrop Overlay */}
        {!isPreviewMode && selectedElementId && (
          <div
            className={`fixed inset-0 bg-gradient-to-r from-black/30 via-black/20 to-transparent backdrop-blur-[2px] z-40 transition-all duration-300 ${
              isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={handleCloseDrawer}
          />
        )}

        {/* Properties Panel Drawer - Left Side */}
        {!isPreviewMode && selectedElementId && (
          <div
            className={`
              fixed left-0 top-0 h-full w-[380px] z-50
              transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
              ${isDrawerOpen ? 'translate-x-0' : '-translate-x-full'}
            `}
          >
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 via-transparent to-transparent pointer-events-none"></div>
            
            {/* Main Drawer */}
            <div className="relative h-full bg-white/95 backdrop-blur-xl border-r border-gray-200/80 shadow-2xl shadow-black/10">
              {/* Animated Top Indicator */}
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-primary-500 via-primary-600 to-primary-500 animate-shimmer"></div>
              
              {/* Subtle Side Glow */}
              <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-primary-500/30 via-primary-400/20 to-transparent"></div>
              
              <PropertiesPanel />
            </div>
          </div>
        )}

      </div>

      {/* Drag Overlay - Shows what's being dragged */}
      <DragOverlay dropAnimation={{
        duration: 200,
        easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
      }}>
        {activeId && draggedElementType ? (
          <div className="bg-gradient-to-br from-white to-primary-50 border-2 border-primary-500 rounded-xl shadow-2xl p-4 backdrop-blur-sm transform rotate-2">
            <div className="flex items-center gap-3">
              <div className="text-3xl animate-bounce">✨</div>
              <div>
                <div className="text-base font-bold text-gray-900">
                  {draggedElementLabel}
                </div>
                <div className="text-xs text-primary-600 font-medium">
                  Drop on canvas to add →
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </DragOverlay>

      {/* Tutorial Overlay */}
      {showTutorial && <TutorialOverlay onClose={() => setShowTutorial(false)} />}
    </DndContext>
  );
};

export default App;

